import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, ArrowLeftRight, Trash2 } from "lucide-react";

// ── Component ────────────────────────────────────────────────

export function UrlEncodeDecode() {
    const [raw, setRaw] = useState("");
    const [encoded, setEncoded] = useState("");
    const [direction, setDirection] = useState<"encode" | "decode">("encode");
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState<string | null>(null);

    useEffect(() => {
        document.title =
            "URL Encode & Decode — Free Online Tool — Ballah Code";
        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
            meta.setAttribute(
                "content",
                "Free URL encoder and decoder. Encode special characters for URLs or decode percent-encoded strings. Includes query string parser.",
            );
        }
    }, []);

    useEffect(() => {
        setError(null);
        if (direction === "encode") {
            if (!raw.trim()) { setEncoded(""); return; }
            try { setEncoded(encodeURIComponent(raw)); } catch { setError("Encoding failed"); }
        } else {
            if (!encoded.trim()) { setRaw(""); return; }
            try { setRaw(decodeURIComponent(encoded)); } catch { setError("Invalid URL-encoded string"); }
        }
    }, [raw, encoded, direction]);

    // Parse query string
    const queryParams = (() => {
        const source = direction === "encode" ? raw : encoded;
        try {
            const qIdx = source.indexOf("?");
            const search = qIdx >= 0 ? source.slice(qIdx) : source;
            const params = new URLSearchParams(search);
            const entries = Array.from(params.entries());
            return entries.length > 0 ? entries : null;
        } catch {
            return null;
        }
    })();

    const copy = async (val: string, key: string) => {
        await navigator.clipboard.writeText(val);
        setCopied(key);
        setTimeout(() => setCopied(null), 2000);
    };

    return (
        <div className="min-h-screen bg-background text-foreground font-sans">
            <nav className="flex items-center justify-between px-8 py-[18px]">
                <Link to="/" className="text-sm font-semibold tracking-[-0.03em] text-zinc-200 hover:text-white transition-colors">ballah code</Link>
                <div className="flex items-center gap-6 text-[13px] text-zinc-600">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">tools</Link>
                    <Link to="/" className="hover:text-zinc-400 transition-colors">home</Link>
                </div>
            </nav>

            <main className="max-w-[1280px] mx-auto px-8 pt-12 pb-24">
                <div className="flex items-center gap-2 text-[13px] text-zinc-600 mb-8">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">Tools</Link>
                    <span className="text-zinc-700">/</span>
                    <span className="text-zinc-400">URL Encode / Decode</span>
                </div>

                <div className="mb-8">
                    <h1 className="text-3xl font-medium tracking-[-0.03em] mb-3">URL Encode & Decode</h1>
                    <p className="text-zinc-500 text-[15px] leading-relaxed max-w-2xl">
                        Encode special characters for URLs or decode percent-encoded strings. Query string parameters are automatically parsed.
                    </p>
                </div>

                <div className="flex items-center gap-3 mb-4">
                    <button
                        onClick={() => setDirection((d) => (d === "encode" ? "decode" : "encode"))}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-white/[0.06] bg-[#111113] text-[13px] text-zinc-400 hover:text-zinc-200 transition-colors"
                    >
                        <ArrowLeftRight size={14} />
                        {direction === "encode" ? "Raw → Encoded" : "Encoded → Raw"}
                    </button>
                    {(raw || encoded) && (
                        <button onClick={() => { setRaw(""); setEncoded(""); setError(null); }} className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-white/[0.06] text-[12px] text-zinc-600 hover:text-zinc-400 transition-colors">
                            <Trash2 size={12} /> Clear
                        </button>
                    )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                    <div className="flex flex-col">
                        <div className="flex items-center justify-between px-4 py-2.5 bg-[#0d0d0f] border border-white/[0.06] border-b-0 rounded-t-xl">
                            <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">{direction === "encode" ? "Raw (input)" : "Raw (output)"}</span>
                            {raw && <button onClick={() => copy(raw, "raw")} className="text-[11px] text-zinc-600 hover:text-zinc-300 transition-colors">{copied === "raw" ? <Check size={11} className="text-green-400" /> : <Copy size={11} />}</button>}
                        </div>
                        <textarea
                            value={raw}
                            onChange={(e) => { if (direction === "encode") setRaw(e.target.value); }}
                            readOnly={direction === "decode"}
                            placeholder="Hello World! → Hello%20World%21"
                            spellCheck={false}
                            className={`w-full min-h-[200px] bg-[#111113] border border-white/[0.06] rounded-b-xl p-4 text-[13px] text-zinc-300 font-mono leading-relaxed outline-none resize-y placeholder:text-zinc-800 transition-colors ${direction === "encode" ? "focus:border-white/[0.1]" : "opacity-80"}`}
                        />
                    </div>

                    <div className="flex flex-col">
                        <div className="flex items-center justify-between px-4 py-2.5 bg-[#0d0d0f] border border-white/[0.06] border-b-0 rounded-t-xl">
                            <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">{direction === "decode" ? "Encoded (input)" : "Encoded (output)"}</span>
                            {encoded && <button onClick={() => copy(encoded, "enc")} className="text-[11px] text-zinc-600 hover:text-zinc-300 transition-colors">{copied === "enc" ? <Check size={11} className="text-green-400" /> : <Copy size={11} />}</button>}
                        </div>
                        <textarea
                            value={encoded}
                            onChange={(e) => { if (direction === "decode") setEncoded(e.target.value); }}
                            readOnly={direction === "encode"}
                            placeholder="Hello%20World%21"
                            spellCheck={false}
                            className={`w-full min-h-[200px] bg-[#111113] border border-white/[0.06] rounded-b-xl p-4 text-[13px] text-zinc-300 font-mono leading-relaxed outline-none resize-y placeholder:text-zinc-800 transition-colors ${direction === "decode" ? "focus:border-white/[0.1]" : "opacity-80"}`}
                        />
                    </div>
                </div>

                {error && <div className="text-[13px] text-red-400 mb-4">{error}</div>}

                {/* Query string params */}
                {queryParams && (
                    <div className="bg-[#111113] border border-white/[0.06] rounded-xl overflow-hidden mb-16">
                        <div className="px-4 py-2.5 border-b border-white/[0.04]">
                            <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">Query Parameters</span>
                        </div>
                        {queryParams.map(([key, value], i) => (
                            <div key={i} className={`flex items-center justify-between px-4 py-2.5 text-[13px] ${i < queryParams.length - 1 ? "border-b border-white/[0.04]" : ""}`}>
                                <span className="text-violet-400 font-mono">{key}</span>
                                <span className="text-zinc-300 font-mono">{value}</span>
                            </div>
                        ))}
                    </div>
                )}

                <section className="max-w-2xl border-t border-white/[0.04] pt-12">
                    <h2 className="text-lg font-medium tracking-[-0.02em] mb-4 text-zinc-300">What is URL encoding?</h2>
                    <div className="space-y-4 text-[14.5px] text-zinc-500 leading-[1.75]">
                        <p>URL encoding (percent-encoding) replaces unsafe ASCII characters with a "%" followed by two hexadecimal digits. This is required when passing special characters like spaces, ampersands, and question marks in URLs.</p>
                        <p>This tool also parses query strings, breaking out individual key-value parameters for easy inspection.</p>
                        <p>Everything runs in your browser. Built by the team behind <Link to="/" className="text-violet-400 hover:text-violet-300 transition-colors">Ballah Code</Link>.</p>
                    </div>
                    <div className="mt-8 flex flex-wrap gap-2">
                        <span className="text-[12px] text-zinc-700 mr-1 self-center">Related →</span>
                        <Link to="/tools/base64" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Base64 Encode / Decode</Link>
                        <Link to="/tools/json-formatter" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">JSON Formatter</Link>
                        <Link to="/tools/hash-generator" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Hash Generator</Link>
                    </div>
                </section>
            </main>

            <footer className="border-t border-white/[0.04] py-5 px-8 flex justify-between max-w-[1280px] mx-auto text-xs text-zinc-800">
                <Link to="/" className="hover:text-zinc-600 transition-colors">ballah code</Link>
                <Link to="/tools" className="hover:text-zinc-600 transition-colors">tools</Link>
                <span>ballah tech</span>
            </footer>
        </div>
    );
}
